import {
  CaretDown,
  CheckCircle,
  Link as LinkIcon,
  TextAlignLeft,
  Trash,
  X,
  XCircle,
} from "@phosphor-icons/react";
import { clsx } from "clsx";

import RenderIf from "common/components/RenderIf";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuList,
  DropdownMenuTrigger,
} from "common/components/ui/DropdownMenu";
import { Tag } from "common/components/ui/Tag";
import type { Keyword as KeywordType, ProfileSearchParams } from "common/types";

import {
  KEYWORD_MODIFIER_OPTIONS,
  KEYWORD_OPTIONS,
} from "../../../../constants";
import useKeywords from "./useKeywords";

export default function Keyword({
  keyword,
  keywords,
  setSearchParams,
}: {
  keyword: KeywordType;
  keywords: ProfileSearchParams["keywords"];
  setSearchParams: (searchParams: Partial<ProfileSearchParams>) => void;
}) {
  const { modifier, text, type } = keyword;
  const { removeKeyword, updateKeywordModifier } = useKeywords(
    keywords,
    setSearchParams,
  );
  const { tagVariant, additionalClasses } = KEYWORD_MODIFIER_OPTIONS[modifier];
  const { icon: TypeIcon } = KEYWORD_OPTIONS[type];
  const isGeneral = type === "general";

  if (modifier === "raw")
    return (
      <Tag
        leftIcon={isGeneral ? <LinkIcon /> : <TypeIcon weight="fill" />}
        variant={tagVariant}
        rightIcon={
          <button
            type="button"
            aria-label="Remove keyword"
            onClick={() => removeKeyword(keyword)}
          >
            <X />
          </button>
        }
      >
        {text}
      </Tag>
    );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Tag
          className={clsx("group transition-colors", additionalClasses)}
          variant={tagVariant}
          leftIcon={<TypeIcon weight={isGeneral ? "regular" : "fill"} />}
          rightIcon={
            <CaretDown className="transition-transform group-data-[state=open]:rotate-180" />
          }
        >
          {text}
        </Tag>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuList>
          <RenderIf condition={modifier !== "required"}>
            <DropdownMenuItem
              onClick={() => updateKeywordModifier(keyword, "required")}
            >
              <CheckCircle />
              Must include
            </DropdownMenuItem>
          </RenderIf>
          <RenderIf condition={modifier !== "excluded"}>
            <DropdownMenuItem
              onClick={() => updateKeywordModifier(keyword, "excluded")}
            >
              <XCircle />
              Must exclude
            </DropdownMenuItem>
          </RenderIf>
          <RenderIf condition={modifier !== "optional"}>
            <DropdownMenuItem
              onClick={() => updateKeywordModifier(keyword, "optional")}
            >
              <TextAlignLeft />
              Make optional
            </DropdownMenuItem>
          </RenderIf>
          <DropdownMenuItem
            variant="danger"
            onClick={() => removeKeyword(keyword)}
          >
            <Trash />
            Remove
          </DropdownMenuItem>
        </DropdownMenuList>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
